import type { HeaderNavAction } from "@lib/types";
import Link from "next/link";
import { useEffect, useRef } from "react";

import { resolveCtaHref } from "@/components/shared/cta";
import { Button } from "@/components/ui/button";
import { icon } from "@/components/ui/icons";

const PersonIcon = icon("Account");

type MobileAuthMenuProps = {
  primaryAction?: HeaderNavAction | null;
  secondaryAction?: HeaderNavAction | null;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function MobileAuthMenu({
  primaryAction,
  secondaryAction,
  isOpen,
  onOpenChange,
}: MobileAuthMenuProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const primaryHref = resolveCtaHref(primaryAction?.href);
  const secondaryHref = resolveCtaHref(secondaryAction?.href);

  const hasPrimaryAction = Boolean(primaryAction?.label && primaryHref);
  const hasSecondaryAction = Boolean(secondaryAction?.label && secondaryHref);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onOpenChange?.(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onOpenChange]);

  const handleLinkClick = () => {
    onOpenChange?.(false);
  };

  const handleToggle = () => {
    onOpenChange?.(!isOpen);
  };

  if (!hasPrimaryAction && !hasSecondaryAction) {
    return null;
  }

  return (
    <div className="relative h-[40px]" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="text-foreground p-padding-element-3xs transition-colors hover:text-neutral-300"
        aria-label="Account menu"
      >
        <PersonIcon className="size-8" />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 z-50 mt-2">
          <div className="absolute top-[-6px] right-4 h-0 w-0 border-r-[6px] border-b-[6px] border-l-[6px] border-r-transparent border-b-white border-l-transparent" />
          <div className="w-[216px] overflow-hidden rounded-lg bg-white shadow-lg">
            <div className="gap-gap-element-sm p-padding-element-md flex flex-col">
              {hasPrimaryAction && primaryHref && primaryAction && (
                <Button
                  asChild
                  variant="primary"
                  className="h-[40px] w-full justify-center rounded-md bg-black text-white"
                  size="sm"
                >
                  <Link
                    href={primaryHref}
                    onClick={handleLinkClick}
                    target={primaryAction.openInNewTab ? "_blank" : undefined}
                    rel={primaryAction.openInNewTab ? "noopener noreferrer" : undefined}
                  >
                    {primaryAction.label}
                  </Link>
                </Button>
              )}
              {hasSecondaryAction && secondaryHref && secondaryAction && (
                <Button
                  asChild
                  variant="secondary"
                  className="w-full justify-center rounded-md border-black text-black"
                  size="sm"
                >
                  <Link
                    href={secondaryHref}
                    onClick={handleLinkClick}
                    target={secondaryAction.openInNewTab ? "_blank" : undefined}
                    rel={secondaryAction.openInNewTab ? "noopener noreferrer" : undefined}
                  >
                    {secondaryAction.label}
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
